import * as React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { t, type MessageKey } from '@reos/i18n';
import { V4Group, V4Pill, V4Row, V4Segmented } from '../ux-v4.js';
import {
  getLead,
  getTerritoryStats,
  letterPreviewUrl,
  listLeads,
  listTerritories,
  setLeadStatus,
  type LeadDetailDto,
  type LeadDto,
  type LeadStatus,
  type ListLeadsQuery,
  type TerritoryDto,
} from '../../lib/acquisition-radar-api.js';
import { createCaseOverProperty } from '../../lib/case-create.js';
import { markGuideSeen } from '../../lib/guide-api.js';
import {
  formatCzk,
  formatDate,
  SIGNAL_KEYS,
  signalLabel,
  SOURCE_KEYS,
  sourceLabel,
  statusLabel,
} from './labels.js';
import { DateField } from '../date-field.js';

/**
 * Krok AKV1 — Tipy: seznam (signal, adresa, trida adresy, stav, datum),
 * filtry, detail (inzerat, nemovitost, vlastnik/adresa podle toho, co radar
 * vrati) a zmena stavu.
 *
 * Krok MRGA1: radar od kroku C10 posila v seznamu tipu portaly (`zdroje`) a den
 * signalu — seznam ma proto sloupec „Zdroj" a datum (AKV1 NÁLEZ 3) — a zna
 * postup maklere Nový → Osloveno → Schůzka → Nabráno / Zamítnutý
 * (AKV1 NÁLEZ 2) vedle dopisoveho toku Převzatý → K dopisu.
 */

/**
 * Prazdny stav: signaly radaru potrebuji historii inzeratu — „neprodalo se"
 * vznika po 60 dnech (`NEPRODALO_SE_DAYS`, akv/signals.py). Proto „do 60 dní".
 */
export const FIRST_LEADS_DAYS = 60;

/**
 * Poradi stavu v segmentech i ve filtru: nejdriv postup maklere (MRGA1), pak
 * dopisovy tok radaru. „K dopisu" musi zustat dosazitelne — tiskova sada na
 * Dopisech bere prave tipy v tomhle stavu.
 */
const STATUS_ORDER: readonly LeadStatus[] = [
  'new',
  'contacted',
  'meeting',
  'won',
  'rejected',
  'claimed',
  'letter_approved',
];

const tr = (key: MessageKey, values?: Record<string, string | number>): string =>
  t(key, { locale: 'cs', ...(values !== undefined ? { values } : {}) });

type Filters = Partial<Pick<ListLeadsQuery, 'status' | 'signalKind' | 'source' | 'territoryId'>> & { sinceDay?: string };

function toQuery(filters: Filters): Partial<ListLeadsQuery> {
  const { sinceDay, ...rest } = filters;
  return { ...rest, ...(sinceDay !== undefined && sinceDay !== '' ? { since: `${sinceDay}T00:00:00Z` } : {}) };
}

function errorText(error: unknown, fallback: MessageKey): string {
  return error instanceof Error && error.message !== '' ? error.message : tr(fallback);
}

export function LeadsTab({ onGoTerritories }: { readonly onGoTerritories: () => void }): React.JSX.Element {
  const [filters, setFilters] = React.useState<Filters>({});
  const [leads, setLeads] = React.useState<readonly LeadDto[] | null>(null);
  const [territories, setTerritories] = React.useState<readonly TerritoryDto[]>([]);
  const [knownListings, setKnownListings] = React.useState<number | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<number | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    listTerritories()
      .then((list) => {
        if (!cancelled) setTerritories(list);
      })
      .catch(() => {
        /* filtr uzemi neni kriticky — seznam tipu jde i bez nej */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    setError(null);
    listLeads(toQuery(filters))
      .then((list) => {
        if (!cancelled) setLeads(list);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(errorText(e, 'webAdmin.acquisitionRadar.leads.loadError'));
      });
    return () => {
      cancelled = true;
    };
  }, [filters]);

  const hasFilters = Object.values(filters).some((v) => v !== undefined && v !== '');
  const isEmpty = leads !== null && leads.length === 0 && !hasFilters;
  /*
   * Krok PRU1 (ROZHODNUTI PRU1-5): radar zatim nic nenasel — neni co otevrit,
   * prohlednuty prazdny seznam je cely krok „Projděte tipy z akvizice".
   */
  React.useEffect(() => {
    if (isEmpty) void markGuideSeen('acquisition');
  }, [isEmpty]);

  // Prazdny stav poctive: co radar o uzemi uz vi (pocet inzeratu ze statistik).
  React.useEffect(() => {
    if (!isEmpty) return;
    const active = territories.filter((tr2) => tr2.active);
    if (active.length === 0) return;
    let cancelled = false;
    Promise.all(active.map((territory) => getTerritoryStats(territory.id)))
      .then((stats) => {
        if (!cancelled) setKnownListings(stats.reduce((sum, s) => sum + s.listingCount, 0));
      })
      .catch(() => {
        /* bez statistik prazdny stav jen nerekne cislo */
      });
    return () => {
      cancelled = true;
    };
  }, [isEmpty, territories]);

  const setFilter = <K extends keyof Filters>(key: K, value: Filters[K] | ''): void => {
    setFilters((prev) => {
      const next = { ...prev };
      if (value === '' || value === undefined) delete next[key];
      else next[key] = value as Filters[K];
      return next;
    });
  };

  const onStatusChanged = (updated: LeadDto): void => {
    setLeads((prev) => prev?.map((l) => (l.id === updated.id ? { ...l, status: updated.status } : l)) ?? null);
  };

  return (
    <div className="rh-v4-groups" data-testid="akv-leads">
      {/*
        Krok UXF2 bod 8 (nalez #22) — CTYRI FILTRY V JEDNE LISTE.
        Do UXF2 kazdy filtr zabiral cely radek karty (`V4FormRow`: popisek
        vlevo, uzky vstup vpravo), takze seznam tipu zacinal az ctyri radky
        pod hlavickou. Popisky jsou nad prvky (sdilena trida `rh-akv-field`)
        a lista se na 390 px zalamuje — zadna nova komponenta.
      */}
      <div className="rh-akv-filterbar" data-testid="akv-leads-filters">
        <label className="rh-akv-field">
          {tr('webAdmin.acquisitionRadar.leads.filterStatus')}
          <select
            className="rh-input"
            value={filters.status ?? ''}
            onChange={(e) => setFilter('status', e.target.value as LeadStatus | '')}
            aria-label={tr('webAdmin.acquisitionRadar.leads.filterStatus')}
            data-testid="akv-filter-status"
          >
            <option value="">{tr('webAdmin.acquisitionRadar.any')}</option>
            {STATUS_ORDER.map((s) => (
              <option key={s} value={s}>
                {statusLabel(s)}
              </option>
            ))}
          </select>
        </label>
        <label className="rh-akv-field">
          {tr('webAdmin.acquisitionRadar.leads.filterSignal')}
          <select
            className="rh-input"
            value={filters.signalKind ?? ''}
            onChange={(e) => setFilter('signalKind', e.target.value)}
            aria-label={tr('webAdmin.acquisitionRadar.leads.filterSignal')}
            data-testid="akv-filter-signal"
          >
            <option value="">{tr('webAdmin.acquisitionRadar.any')}</option>
            {Object.keys(SIGNAL_KEYS).map((code) => (
              <option key={code} value={code}>
                {signalLabel(code)}
              </option>
            ))}
          </select>
        </label>
        <label className="rh-akv-field">
          {tr('webAdmin.acquisitionRadar.leads.filterSource')}
          <select
            className="rh-input"
            value={filters.source ?? ''}
            onChange={(e) => setFilter('source', e.target.value)}
            aria-label={tr('webAdmin.acquisitionRadar.leads.filterSource')}
            data-testid="akv-filter-source"
          >
            <option value="">{tr('webAdmin.acquisitionRadar.any')}</option>
            {Object.keys(SOURCE_KEYS).map((code) => (
              <option key={code} value={code}>
                {sourceLabel(code)}
              </option>
            ))}
          </select>
        </label>
        {territories.length > 1 ? (
          <label className="rh-akv-field">
            {tr('webAdmin.acquisitionRadar.leads.filterTerritory')}
            <select
              className="rh-input"
              value={filters.territoryId ?? ''}
              onChange={(e) => setFilter('territoryId', e.target.value === '' ? '' : Number(e.target.value))}
              aria-label={tr('webAdmin.acquisitionRadar.leads.filterTerritory')}
              data-testid="akv-filter-territory"
            >
              <option value="">{tr('webAdmin.acquisitionRadar.any')}</option>
              {territories.map((territory) => (
                <option key={territory.id} value={territory.id}>
                  {territory.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label className="rh-akv-field">
          {tr('webAdmin.acquisitionRadar.leads.filterSince')}
          <DateField
            className="rh-input"
            value={filters.sinceDay ?? ''}
            onValueChange={(next) => setFilter('sinceDay', next)}
            ariaLabel={tr('webAdmin.acquisitionRadar.leads.filterSince')}
            testId="akv-filter-since"
          />
        </label>
      </div>

      {error !== null ? (
        <p className="rh-akv-note rh-akv-note--warn" role="alert" data-testid="akv-leads-error">
          {error}
        </p>
      ) : null}

      {leads === null && error === null ? <p className="rh-akv-hint">{tr('webAdmin.acquisitionRadar.loading')}</p> : null}

      {isEmpty ? (
        <section className="rh-v4-group rh-v4-card" data-testid="akv-leads-empty" data-guide="acquisition">
          <h2 className="rh-akv-title">{tr('webAdmin.acquisitionRadar.leads.emptyTitle')}</h2>
          {territories.length === 0 ? (
            <>
              <p className="rh-akv-text">{tr('webAdmin.acquisitionRadar.leads.emptyNoTerritory')}</p>
              <button type="button" className="rh-v4-btn rh-v4-btn--tinted" onClick={onGoTerritories}>
                {tr('webAdmin.acquisitionRadar.leads.goTerritories')}
              </button>
            </>
          ) : (
            <>
              <p className="rh-akv-text">
                {tr('webAdmin.acquisitionRadar.leads.emptyCollecting', { days: FIRST_LEADS_DAYS })}
              </p>
              {knownListings !== null ? (
                <p className="rh-akv-text" data-testid="akv-leads-known">
                  {tr('webAdmin.acquisitionRadar.leads.emptyKnown', { count: knownListings })}
                </p>
              ) : null}
            </>
          )}
        </section>
      ) : null}

      {leads !== null && leads.length === 0 && hasFilters ? (
        <p className="rh-akv-hint" data-testid="akv-leads-filtered-empty">
          {tr('webAdmin.acquisitionRadar.leads.emptyFiltered')}
        </p>
      ) : null}

      {leads !== null && leads.length > 0 ? (
        <div data-guide="acquisition">
        <V4Group label={tr('webAdmin.acquisitionRadar.leads.listLabel')} testId="akv-leads-list">
          {leads.map((lead) => (
            <React.Fragment key={lead.id}>
              <V4Row
                testId={`akv-lead-${String(lead.id)}`}
                title={lead.postalAddress ?? tr('webAdmin.acquisitionRadar.leads.addressUnknown')}
                sub={
                  <>
                    {[
                      signalLabel(lead.signalKind),
                      lead.addressClass !== null
                        ? tr('webAdmin.acquisitionRadar.leads.addressClass', { cls: lead.addressClass })
                        : null,
                      // Datum = den signalu (jak je tip cerstvy); kdyz ho radar
                      // neposila, zbyva datum zalozeni tipu.
                      formatDate(lead.signalDay ?? lead.createdAt),
                    ]
                      .filter((part): part is string => part !== null && part !== '')
                      .join(' · ')}
                    {lead.portals.length > 0 ? (
                      <>
                        {' · '}
                        <span data-testid={`akv-lead-source-${String(lead.id)}`}>
                          {`${tr('webAdmin.acquisitionRadar.leads.source')}: ${lead.portals.map(sourceLabel).join(', ')}`}
                        </span>
                      </>
                    ) : null}
                  </>
                }
                value={<V4Pill tone={lead.status === 'new' ? 'accent' : 'neutral'}>{statusLabel(lead.status)}</V4Pill>}
                chevron
                onClick={() => setSelected(selected === lead.id ? null : lead.id)}
              />
              {selected === lead.id ? <LeadDetail id={lead.id} onStatusChanged={onStatusChanged} /> : null}
            </React.Fragment>
          ))}
        </V4Group>
        </div>
      ) : null}
    </div>
  );
}

function LeadDetail({
  id,
  onStatusChanged,
}: {
  readonly id: number;
  readonly onStatusChanged: (lead: LeadDto) => void;
}): React.JSX.Element {
  const [detail, setDetail] = React.useState<LeadDetailDto | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);
  /** Krok PIL1 (vada V13): „Převzít do zakázky" — bezi, dokud zakazka nevznikne. */
  const [takingOver, setTakingOver] = React.useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    let cancelled = false;
    getLead(id)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        // Krok PRU1: otevreny tip odskrtne krok „Projděte tipy z akvizice".
        void markGuideSeen('acquisition');
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(errorText(e, 'webAdmin.acquisitionRadar.leads.loadError'));
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  /**
   * Krok PIL1 (vada V13) — PREVZETI TIPU DO ZAKAZKY.
   *
   * Posloupnost je JEDNA (`lib/case-create.ts`): nemovitost z adresy tipu →
   * zakazka nad ni (pravidlo O3). Teprve kdyz zakazka vznikne, oznaci se tip
   * „Převzatý" — opacne poradi by pri selhani nechalo tip oznaceny bez
   * zakazky. Druh nemovitosti radar neposila, proto `building`: je to
   * nejcastejsi tip (dum/byt v dome) a prověření ho stejne urci znovu.
   */
  const takeOver = async (): Promise<void> => {
    if (detail === null || takingOver) return;
    const address = detail.postalAddress;
    if (address === null || address.trim() === '') {
      setError(tr('webAdmin.acquisitionRadar.leads.takeOverNoAddress'));
      return;
    }
    setTakingOver(true);
    setError(null);
    try {
      const { caseId } = await createCaseOverProperty({
        /*
         * Krok P8F1 bod 2: idempotencni klic JEDNOHO prevzeti tipu. Klic je
         * ID tipu, takze druhe klepnuti na „Převzít" (i po sitove chybe)
         * vrati tutez zakazku misto druhe nad tymz domem.
         */
        submissionKey: `lead-${id}`,
        title: address,
        addressQuery: address,
        propertyKind: 'building',
        caseType: 'brokerage_sale',
      });
      const updated = await setLeadStatus(id, 'claimed').catch(() => null);
      if (updated !== null) {
        setDetail({ ...detail, status: updated.status });
        onStatusChanged(updated);
      }
      await navigate({ to: '/zakazky/$id', params: { id: caseId } });
    } catch (e) {
      setError(errorText(e, 'webAdmin.acquisitionRadar.leads.takeOverFailed'));
    } finally {
      setTakingOver(false);
    }
  };

  const changeStatus = (status: LeadDto['status']): void => {
    if (status === 'unknown' || detail === null) return;
    setSaved(false);
    setError(null);
    setLeadStatus(id, status)
      .then((updated) => {
        setDetail({ ...detail, status: updated.status });
        onStatusChanged(updated);
        setSaved(true);
      })
      .catch((e: unknown) => setError(errorText(e, 'webAdmin.acquisitionRadar.saveError')));
  };

  return (
    <div className="rh-akv-detail" data-testid={`akv-lead-detail-${String(id)}`}>
      {error !== null ? (
        <p className="rh-akv-note rh-akv-note--warn" role="alert">
          {error}
        </p>
      ) : null}
      {detail === null ? (
        error === null ? <p className="rh-akv-hint">{tr('webAdmin.acquisitionRadar.loading')}</p> : null
      ) : (
        <>
          <dl className="rh-akv-dl">
            <dt>{tr('webAdmin.acquisitionRadar.leads.signal')}</dt>
            <dd>
              {signalLabel(detail.signal?.kind ?? detail.signalKind)}
              {detail.signal?.day ? ` · ${formatDate(detail.signal.day)}` : ''}
            </dd>
            {detail.signal?.reason ?? detail.reason ? (
              <>
                <dt>{tr('webAdmin.acquisitionRadar.leads.reason')}</dt>
                <dd>{detail.signal?.reason ?? detail.reason}</dd>
              </>
            ) : null}
            <dt>{tr('webAdmin.acquisitionRadar.leads.owner')}</dt>
            <dd data-testid="akv-lead-address">
              {detail.postalAddress ?? tr('webAdmin.acquisitionRadar.leads.addressUnknown')}
              {detail.addressClass !== null
                ? ` · ${tr('webAdmin.acquisitionRadar.leads.addressClass', { cls: detail.addressClass })}`
                : ''}
              {detail.addressConfidence !== null
                ? ` · ${tr('webAdmin.acquisitionRadar.leads.addressConfidence', { value: detail.addressConfidence })}`
                : ''}
            </dd>
            <dt>{tr('webAdmin.acquisitionRadar.leads.property')}</dt>
            <dd>
              {detail.propertyId !== null
                ? tr('webAdmin.acquisitionRadar.leads.propertyMatched', { id: detail.propertyId })
                : tr('webAdmin.acquisitionRadar.leads.propertyUnmatched')}
            </dd>
          </dl>

          {detail.sources.length > 0 ? (
            <>
              <h3 className="rh-akv-subtitle">{tr('webAdmin.acquisitionRadar.leads.sources')}</h3>
              <ul className="rh-akv-list" data-testid="akv-lead-sources">
                {detail.sources.map((source, index) => (
                  <li key={`${source.kind}-${String(index)}`}>
                    {source.portal ?? source.kind}
                    {source.confidence !== null ? ` · ${source.confidence}` : ''}
                    {source.url !== null ? (
                      <>
                        {' · '}
                        <a href={source.url} target="_blank" rel="noreferrer noopener">
                          {tr('webAdmin.acquisitionRadar.leads.openListing')}
                        </a>
                      </>
                    ) : null}
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          {detail.priceHistory.length > 0 ? (
            <>
              <h3 className="rh-akv-subtitle">{tr('webAdmin.acquisitionRadar.leads.priceHistory')}</h3>
              <ul className="rh-akv-list" data-testid="akv-lead-prices">
                {detail.priceHistory.map((point) => (
                  <li key={`${String(point.listingId)}-${point.day}`}>
                    {formatDate(point.day)} · {formatCzk(point.price.amount)}
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          <h3 className="rh-akv-subtitle">{tr('webAdmin.acquisitionRadar.leads.changeStatus')}</h3>
          <div className="rh-akv-scroll-x">
            <V4Segmented<LeadStatus>
              value={detail.status === 'unknown' ? 'new' : detail.status}
              options={STATUS_ORDER.map((s) => ({ value: s, label: statusLabel(s) }))}
              onChange={changeStatus}
              label={tr('webAdmin.acquisitionRadar.leads.changeStatus')}
              testId={`akv-lead-status-${String(id)}`}
            />
          </div>
          {saved ? (
            <p className="rh-akv-hint" role="status">
              {tr('webAdmin.acquisitionRadar.saved')}
            </p>
          ) : null}
          <p className="rh-akv-actions">
            {/*
              Krok PIL1 (vada V13): z tipu vedl jen dopis k tisku — prevzit ho
              do zakazky neslo (snimek `GEN1/5-03`), takze makléřka adresu
              prepisovala do pruvodce „Nová". Zakazka vznika NAD NEMOVITOSTI
              (pravidlo O3, `lib/case-create.ts`) a tip se oznaci „Převzatý".
            */}
            <button
              type="button"
              className="rh-v4-btn rh-v4-btn--filled"
              disabled={takingOver}
              onClick={() => void takeOver()}
              data-testid={`akv-lead-take-over-${String(id)}`}
            >
              {takingOver
                ? tr('webAdmin.acquisitionRadar.leads.takeOverBusy')
                : tr('webAdmin.acquisitionRadar.leads.takeOver')}
            </button>
            <a
              className="rh-v4-btn rh-v4-btn--tinted"
              href={letterPreviewUrl(id)}
              target="_blank"
              rel="noreferrer"
              data-testid={`akv-lead-letter-${String(id)}`}
            >
              {tr('webAdmin.acquisitionRadar.leads.letterPreview')}
            </a>
          </p>
        </>
      )}
    </div>
  );
}
